import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, ExternalLink, MessageCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

// Spice data
const spices = [
  {
    id: 'red-chilli',
    name: 'Red Chilli Powder',
    malayalamName: 'മുളകുപൊടി',
    shortDesc: 'The foundation of Indian cooking, bringing heat and vibrant red color to your dishes.',
    colorProfile: 'Deep red to bright red',
    tasteProfile: 'Medium to high heat with earthy undertones',
    bestFor: 'Sambar, fish curry, chicken masala, biriyani, everyday South Indian cooking',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp',
  },
  {
    id: 'coriander',
    name: 'Coriander Powder',
    malayalamName: 'മല്ലിപ്പൊടി',
    shortDesc: 'An essential base spice with citrusy, earthy aroma that forms the backbone of Indian gravies.',
    colorProfile: 'Light brown',
    tasteProfile: 'Citrusy, earthy, slightly sweet',
    bestFor: 'Curries, marinades, spice blends, gravies, rasam, sambar',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Coriander%20Powder.webp',
  },
  {
    id: 'turmeric',
    name: 'Turmeric Powder',
    malayalamName: 'മഞ്ഞൾപ്പൊടി',
    shortDesc: 'The golden spice that adds color, flavor, and health benefits to every dish.',
    colorProfile: 'Golden yellow to deep orange-yellow',
    tasteProfile: 'Earthy, slightly bitter, warm',
    bestFor: 'Dal, curries, rice dishes, marinades, everyday cooking, health drinks',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Turmeric%20Powder.webp',
  },
  {
    id: 'kashmiri-chilli',
    name: 'Kashmiri Chilli Powder',
    malayalamName: 'കാശ്മീരി മുളകുപൊടി',
    shortDesc: 'Prized for its brilliant red color with mild heat - perfect when you want color without overpowering spice.',
    colorProfile: 'Bright vibrant red',
    tasteProfile: 'Mild heat with slightly sweet notes',
    bestFor: 'Biriyani, tandoori dishes, gravies where color matters, restaurant-style curries',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Kashmirii%20Chilli%20Powder.webp',
  }
];

// Placeholders for future spices
const comingSoonSpices = [
  { id: 'black-pepper', name: 'Black Pepper', malayalamName: 'കുരുമുളക്' },
  { id: 'cloves', name: 'Cloves', malayalamName: 'ഗ്രാമ്പൂ' },
  { id: 'cardamom', name: 'Cardamom', malayalamName: 'ഏലം' }
];

// Variants data
const variants = {
  chilli: [
    {
      name: '5531 Deluxe',
      color: 'Bright red',
      aroma: 'Rich, pungent',
      heat: 'Medium (5-6/10)',
      bestUse: 'Export quality, restaurant dishes, premium cooking',
      agniyaUses: true
    },
    {
      name: 'Kashmiri',
      color: 'Bright vibrant red',
      aroma: 'Mild, slightly sweet',
      heat: 'Low (1-2/10)',
      bestUse: 'Color-focused dishes, biriyani, tandoori',
      agniyaUses: true
    },
    {
      name: 'Byadgi',
      color: 'Deep red',
      aroma: 'Rich, aromatic',
      heat: 'Medium (4-5/10)',
      bestUse: 'South Indian curries, rasam, sambar'
    },
    {
      name: 'Guntur',
      color: 'Dark red',
      aroma: 'Pungent, intense',
      heat: 'High (7-8/10)',
      bestUse: 'Andhra cuisine, spicy pickles, high-heat dishes'
    },
    {
      name: 'Teja',
      color: 'Bright red',
      aroma: 'Sharp, spicy',
      heat: 'Very High (9/10)',
      bestUse: 'Extra spicy curries, restaurant-style heat'
    },
    {
      name: 'Jwala',
      color: 'Light red when dried',
      aroma: 'Very pungent',
      heat: 'High (30,000–50,000 SHU)',
      bestUse: 'Daily cooking in Gujarat, pickles, traditional preparations'
    },
    {
      name: 'Kanthari (Bird\'s Eye)',
      color: 'Bright red',
      aroma: 'Extremely pungent',
      heat: 'Very High (100,000–200,000 SHU)',
      bestUse: 'Kerala & Tamil Nadu cuisine, small amounts for intense heat'
    }
  ],
  turmeric: [
    {
      name: 'Lakadong (Meghalaya)',
      color: 'Bright golden',
      aroma: 'Intense, aromatic',
      curcumin: 'Very High (7-12%)',
      bestUse: 'Premium cooking, health drinks, medicinal use, export quality'
    },
    {
      name: 'Alleppey Finger (Kerala)',
      color: 'Bright yellow',
      aroma: 'Strong, earthy',
      curcumin: 'High (4-7%)',
      bestUse: 'European & American markets, bright color dishes, traditional Kerala cooking'
    },
    {
      name: 'Salem Local (Tamil Nadu)',
      color: 'Deep orange',
      aroma: 'Rich, warm',
      curcumin: 'High (4.75%)',
      bestUse: 'Commercial cooking, South Indian dishes, vibrant color preparations'
    },
    {
      name: 'Erode Local (Tamil Nadu)',
      color: 'Deep golden-orange',
      aroma: 'Consistent, earthy',
      curcumin: 'Moderate (3-4%)',
      bestUse: 'High-volume commercial use, consistent quality, everyday cooking',
      agniyaUses: true
    },
    {
      name: 'Rajapore & Sangli (Maharashtra)',
      color: 'Deep orange',
      aroma: 'Strong, medicinal',
      curcumin: 'High (variable)',
      bestUse: 'Medicinal preparations, traditional Ayurvedic cooking, premium dishes'
    },
    {
      name: 'Nizamabad (Telangana)',
      color: 'Classic yellow',
      aroma: 'Mild, pleasant',
      curcumin: 'Moderate (2-4%)',
      bestUse: 'Commercial cooking, everyday use, bulk preparations'
    }
  ],
  coriander: [
    {
      name: 'Gujarat',
      color: 'Light brown',
      aroma: 'Aromatic, fresh',
      taste: 'Sweet, balanced',
      bestUse: 'All-purpose cooking, spice blends, commercial use',
      agniyaUses: true
    },
    {
      name: 'Rajasthan',
      color: 'Light brown',
      aroma: 'Strong citrus notes',
      taste: 'Bold, aromatic',
      bestUse: 'North Indian curries, dry spice blends'
    },
    {
      name: 'Madhya Pradesh',
      color: 'Medium brown',
      aroma: 'Balanced, earthy',
      taste: 'Mild, versatile',
      bestUse: 'Everyday gravies, South Indian cuisine'
    },
    {
      name: 'Local Blends',
      color: 'Varies',
      aroma: 'Region-specific',
      taste: 'Custom profiles',
      bestUse: 'Traditional regional recipes'
    }
  ]
};

// Quality checks
const qualityChecks = [
  {
    title: 'Water Test',
    description: 'Pure spices will settle or dissolve naturally in water without leaving artificial color stains on your fingers or container.',
    icon: '💧'
  },
  {
    title: 'Aroma Check',
    description: 'Fresh spices have a strong, natural aroma. If there\'s little to no smell, the spices might be old or adulterated.',
    icon: '👃'
  },
  {
    title: 'Texture Check',
    description: 'Quality spice powder should be fine and consistent. Grittiness or clumps may indicate impurities or poor processing.',
    icon: '✋'
  },
  {
    title: 'Color Observation',
    description: 'Natural spices have earthy, rich colors. Overly bright or artificial-looking colors may indicate added dyes.',
    icon: '👁️'
  }
];

const SpicesGuidePage = () => {
  const [activeVariantTab, setActiveVariantTab] = useState('chilli');

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi, help me choose the right spice");
    window.open(`https://wa.me/918086075558?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Spices Guide - Know Your Spices Before You Buy | Agniya Spices</title>
        <meta 
          name="description" 
          content="Learn how to choose the right spices for Indian cooking. Comprehensive guide to chilli powder, turmeric, coriander, and more. Understand quality, variants, and usage for Kerala and pan-India cuisines." 
        />
        <meta 
          name="keywords" 
          content="spice guide, how to choose spices, chilli powder guide, turmeric guide, Indian spices guide, Kerala spices, Kashmiri chilli, spice quality, spice variants" 
        />
        <link rel="canonical" href="https://agniyaspices.com/spices-guide" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Spices Guide - Know Your Spices Before You Buy" />
        <meta property="og:description" content="Learn how to choose the right spices for Indian cooking. Comprehensive guide to chilli powder, turmeric, coriander, and more." />
        <meta property="og:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />
        <meta property="og:url" content="https://agniyaspices.com/spices-guide" />
        <meta property="og:site_name" content="Agniya Spices" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Spices Guide - Agniya Spices" />
        <meta name="twitter:description" content="Comprehensive guide to choosing the right spices for Indian cooking." />
        <meta name="twitter:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />

        {/* CollectionPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Spices Guide - Know Your Spices Before You Buy",
            "url": "https://agniyaspices.com/spices-guide",
            "description": "Comprehensive guide to Indian spices: chilli powder, turmeric, coriander, Kashmiri chilli and more. Learn colour, flavour and best uses.",
            "publisher": {
              "@type": "Organization",
              "name": "Agniya Spices"
            }
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://agniyaspices.com" },
              { "@type": "ListItem", "position": 2, "name": "Spices Guide", "item": "https://agniyaspices.com/spices-guide" }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">
              AGNIYA SPICES
            </div>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Home</Link>
            <Link to="/#about" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">About Us</Link>
            
            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#4a3a33] hover:text-[#8B2820] transition-colors focus:outline-none">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/spices-guide" className="text-[#8B2820] font-semibold transition-colors">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Testimonials</Link>
            <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">FAQ</Link>
            <Link to="/#quality" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Quality</Link>
            <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Contact Us</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center mb-6">
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/20">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              Know Your Spices Before You Buy
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Learn how to choose the right spices for Indian cooking. From everyday Kerala dishes 
              to cuisines across India, this guide helps you understand quality, usage, and selection.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => document.getElementById('explore-spices')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="bg-white text-[#8B2820] hover:bg-white/90 px-8 py-6 text-lg"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Explore Guides
              </Button>
              <Button 
                onClick={() => document.getElementById('variants')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/20 px-8 py-6 text-lg"
              >
                Browse Variants
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Explore by Spice Section */}
      <section id="explore-spices" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
              Explore by Spice
            </h2>
            <p className="text-lg text-[#4a3a33] max-w-2xl mx-auto">
              Understand each spice's characteristics and find the perfect match for your cooking needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {spices.map((spice) => (
              <Card 
                key={spice.id} 
                className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group flex flex-col"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={spice.image} 
                    alt={spice.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                      {spice.name}
                    </h3>
                    <p className="text-white/90 text-sm">{spice.malayalamName}</p>
                  </div>
                </div>
                <CardContent className="p-6 flex flex-col flex-grow">
                  <p className="text-[#4a3a33] mb-4 leading-relaxed min-h-[3rem]">
                    {spice.shortDesc}
                  </p>
                  
                  <div className="space-y-3 mb-6 flex-grow">
                    <div>
                      <span className="font-semibold text-[#8B2820]">Color:</span>
                      <span className="text-[#4a3a33] ml-2">{spice.colorProfile}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-[#8B2820]">Taste:</span>
                      <span className="text-[#4a3a33] ml-2">{spice.tasteProfile}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-[#8B2820]">Best Used For:</span>
                      <span className="text-[#4a3a33] ml-2">{spice.bestFor}</span>
                    </div>
                  </div>

                  <Button 
                    onClick={handleWhatsAppClick}
                    variant="outline" 
                    className="w-full border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820]/10"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Ask About This Spice
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Coming Soon Placeholders */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {comingSoonSpices.map((spice) => (
              <Card 
                key={spice.id}
                className="border-2 border-dashed border-[#8B2820]/30 bg-[#f7f5f2] hover:border-[#8B2820]/50 transition-colors"
              >
                <CardContent className="p-6 text-center">
                  <Badge className="bg-amber-500 hover:bg-amber-600 text-white mb-3">
                    Coming Soon
                  </Badge>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-1">
                    {spice.name}
                  </h3>
                  <p className="text-sm text-[#736c64]">{spice.malayalamName}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Spice Variants Section */}
      <section id="variants" className="py-20 bg-[#f7f5f2]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
              Understanding Spice Variants
            </h2>
            <p className="text-lg text-[#4a3a33] max-w-3xl mx-auto">
              Different regions produce different varieties of the same spice. Learn which variant 
              works best for your cooking style.
            </p>
          </div>

          {/* Variant Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button
              onClick={() => setActiveVariantTab('chilli')}
              variant={activeVariantTab === 'chilli' ? 'default' : 'outline'}
              className={activeVariantTab === 'chilli' 
                ? 'bg-[#8B2820] text-white' 
                : 'border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820]/10'}
            >
              Chilli Variants
            </Button>
            <Button
              onClick={() => setActiveVariantTab('turmeric')}
              variant={activeVariantTab === 'turmeric' ? 'default' : 'outline'}
              className={activeVariantTab === 'turmeric' 
                ? 'bg-[#8B2820] text-white' 
                : 'border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820]/10'}
            >
              Turmeric Types
            </Button>
            <Button
              onClick={() => setActiveVariantTab('coriander')}
              variant={activeVariantTab === 'coriander' ? 'default' : 'outline'}
              className={activeVariantTab === 'coriander' 
                ? 'bg-[#8B2820] text-white' 
                : 'border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820]/10'}
            >
              Coriander Sources
            </Button>
          </div>

          {/* Chilli Variants */}
          {activeVariantTab === 'chilli' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {variants.chilli.map((variant, index) => (
                <Card key={index} className={`border-none shadow-lg hover:shadow-xl transition-shadow ${variant.agniyaUses ? 'bg-gradient-to-br from-[#8B2820]/5 to-white ring-2 ring-[#8B2820]/30' : 'bg-white'}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-2xl text-[#8B2820]">{variant.name}</CardTitle>
                      {variant.agniyaUses && (
                        <Badge className="bg-[#8B2820] text-white hover:bg-[#6d1f19]">
                          Agniya Uses
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Color</p>
                      <p className="text-sm text-[#736c64]">{variant.color}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Aroma/Taste</p>
                      <p className="text-sm text-[#736c64]">{variant.aroma}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Heat Level</p>
                      <p className="text-sm text-[#736c64]">{variant.heat}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Best Use</p>
                      <p className="text-sm text-[#736c64]">{variant.bestUse}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Turmeric Variants */}
          {activeVariantTab === 'turmeric' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {variants.turmeric.map((variant, index) => (
                <Card key={index} className={`border-none shadow-lg hover:shadow-xl transition-shadow ${variant.agniyaUses ? 'bg-gradient-to-br from-[#8B2820]/5 to-white ring-2 ring-[#8B2820]/30' : 'bg-white'}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-2xl text-[#8B2820]">{variant.name}</CardTitle>
                      {variant.agniyaUses && (
                        <Badge className="bg-[#8B2820] text-white hover:bg-[#6d1f19]">
                          Agniya Uses
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Color</p>
                      <p className="text-sm text-[#736c64]">{variant.color}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Aroma</p>
                      <p className="text-sm text-[#736c64]">{variant.aroma}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Curcumin Content</p>
                      <p className="text-sm text-[#736c64]">{variant.curcumin}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Best Use</p>
                      <p className="text-sm text-[#736c64]">{variant.bestUse}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Coriander Variants */}
          {activeVariantTab === 'coriander' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {variants.coriander.map((variant, index) => (
                <Card key={index} className={`border-none shadow-lg hover:shadow-xl transition-shadow ${variant.agniyaUses ? 'bg-gradient-to-br from-[#8B2820]/5 to-white ring-2 ring-[#8B2820]/30' : 'bg-white'}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-2xl text-[#8B2820]">{variant.name}</CardTitle>
                      {variant.agniyaUses && (
                        <Badge className="bg-[#8B2820] text-white hover:bg-[#6d1f19]">
                          Agniya Uses
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Color</p>
                      <p className="text-sm text-[#736c64]">{variant.color}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Aroma</p>
                      <p className="text-sm text-[#736c64]">{variant.aroma}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Taste Profile</p>
                      <p className="text-sm text-[#736c64]">{variant.taste}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#4a3a33]">Best Use</p>
                      <p className="text-sm text-[#736c64]">{variant.bestUse}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quality & Purity Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
              How to Check Spice Quality
            </h2>
            <p className="text-lg text-[#4a3a33] max-w-3xl mx-auto">
              Simple home tests to help you identify pure, quality spices. These checks work for most ground spices.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityChecks.map((check, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-2xl transition-shadow bg-[#f7f5f2]">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4">{check.icon}</div>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-4">
                    {check.title}
                  </h3>
                  <p className="text-[#736c64] leading-relaxed">
                    {check.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 p-6 bg-[#8B2820]/5 rounded-lg border-l-4 border-[#8B2820] max-w-4xl mx-auto">
            <p className="text-[#4a3a33] leading-relaxed">
              <strong className="text-[#8B2820]">Note:</strong> At Agniya Spices, every batch undergoes 
              quality checks to ensure purity, natural color, and freshness. We believe in transparency 
              and encourage our customers to always verify the quality of spices they purchase, wherever 
              they buy from.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Need Help Choosing the Right Spice?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Our team is here to guide you. Whether you're cooking for your family or running a restaurant, 
            we'll help you find the perfect spice for your needs.
          </p>
          <Button 
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-white text-[#8B2820] hover:bg-white/90 px-12 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105"
          >
            <MessageCircle className="mr-2 h-6 w-6" />
            Ask on WhatsApp
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SpicesGuidePage;
